"use client";

import { createContext, useContext, type FC, type ReactNode } from "react";
import Skeleton from "@mui/material/Skeleton";
import { styled } from "@mui/material/styles";
import { useField } from "formik";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronRight } from "@fortawesome/pro-solid-svg-icons/faChevronRight";

import { useFaIcon } from "@portal/app/_components/hooks/useFaIcon";

export interface PictureChoiceOption {
  label: string;
  value: string | number | boolean;
  icon?: string;
}

interface PictureChoiceContextShape {
  name?: string;
  value: string | boolean | number | null;
  setValue?: (newValue: string | boolean | number | null) => void;
  reselectValue?: (value: string | boolean | number | null) => void;
}

const PictureChoiceContext = createContext<PictureChoiceContextShape>({
  value: null,
  setValue: undefined,
  reselectValue: undefined,
});

interface PictureChoiceGroupProps extends PictureChoiceContextShape {
  children: ReactNode;
  hasIcons?: boolean;
}

export const PictureChoiceGroup: FC<PictureChoiceGroupProps> = ({
  name,
  value,
  setValue,
  reselectValue,
  children,
  hasIcons = false,
}) => (
  <GroupWrapper $hasIcons={hasIcons}>
    <PictureChoiceContext.Provider value={{ name, value, setValue, reselectValue }}>
      {children}
    </PictureChoiceContext.Provider>
  </GroupWrapper>
);

interface PictureChoiceItemProps {
  value: string | boolean | number | null;
  icon?: string;
  children: ReactNode;
  disabled?: boolean;
  "data-testid"?: string;
}

export const PictureChoiceItem: FC<PictureChoiceItemProps> = ({
  value,
  icon,
  children,
  disabled,
  "data-testid": dataTestid = `picture-choice-${value}`,
}) => {
  const { name, value: currentValue, setValue, reselectValue } =
    useContext(PictureChoiceContext);
  const isActive = value === currentValue;
  const { iconDef, isLoading: isIconLoading } = useFaIcon(icon);
  const hasIconSlot = Boolean(icon && (iconDef || isIconLoading));

  return (
    <ChoiceLabel
      $hasIcon={hasIconSlot}
      className={isActive ? "active" : ""}
      $disabled={Boolean(disabled)}
    >
      <ChoiceInput
        type="radio"
        name={name}
        value={value?.toString() ?? ""}
        checked={isActive}
        onClick={() => {
          // Native radios do not emit change when their checked option is
          // activated again. Re-run the selection callback so CTA-less
          // auto-advance steps remain actionable after Back.
          if (isActive) reselectValue?.(value);
        }}
        onKeyDown={(event) => {
          // Browsers also suppress activation for Space on an already-checked
          // radio. Keep native arrow-key behavior and make Space actionable.
          if (isActive && reselectValue && event.key === " ") {
            event.preventDefault();
            reselectValue(value);
          }
        }}
        onChange={() => setValue?.(value)}
        disabled={disabled}
        data-testid={dataTestid}
      />
      {hasIconSlot && (
        <IconWrapper
          className="picture-choice-icon"
          data-testid={`${dataTestid}-icon`}
          aria-hidden="true"
        >
          {iconDef ? (
            <FontAwesomeIcon icon={iconDef} />
          ) : (
            <IconPlaceholder
              className="picture-choice-icon-placeholder"
              variant="circular"
              animation="pulse"
            />
          )}
        </IconWrapper>
      )}
      <LabelText className="picture-choice-label" $hasIcon={hasIconSlot}>
        {children}
      </LabelText>
      <ChevronIcon className="picture-choice-chevron" icon={faChevronRight} aria-hidden="true" />
    </ChoiceLabel>
  );
};

interface PictureChoiceGroupWithFormikProps {
  name: string;
  children: ReactNode;
  onChange?: (newValue: string | number | boolean | null) => void;
  onReselect?: (value: string | number | boolean | null) => void;
  hasIcons?: boolean;
}

export const PictureChoiceGroupWithFormik: FC<PictureChoiceGroupWithFormikProps> = ({
  children,
  name,
  onChange,
  onReselect,
  hasIcons,
}) => {
  const [field, , helpers] = useField(name);

  const handleChange = async (newValue: string | number | boolean | null) => {
    await helpers.setValue(newValue, true);
    await helpers.setTouched(true);
    if (onChange) {
      onChange(newValue);
    }
  };

  return (
    <PictureChoiceGroup
      name={name}
      value={field.value}
      setValue={handleChange}
      reselectValue={onReselect}
      hasIcons={hasIcons}
    >
      {children}
    </PictureChoiceGroup>
  );
};

// Styled components
const GroupWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "$hasIcons",
})<{ $hasIcons: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: ${({ $hasIcons }) => ($hasIcons ? "12px" : "10px")};
  margin-top: 0;
  width: 100%;
`;

const ChoiceLabel = styled("label", {
  shouldForwardProp: (prop) => prop !== "$hasIcon" && prop !== "$disabled",
})<{ $hasIcon: boolean; $disabled: boolean }>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: flex-start;
  gap: ${({ $hasIcon }) => ($hasIcon ? "14px" : "12px")};
  text-align: left;
  padding: ${({ $hasIcon }) => ($hasIcon ? "18px" : "16px 18px")};
  width: 100%;
  min-height: 56px;
  border: 1px solid ${({ theme }) => theme.palette.dream.borderColor};
  border-radius: 20px;
  background-color: ${({ theme }) => theme.palette.common.white};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    box-shadow 0.2s ease;

  &:hover {
    border-color: ${({ theme }) => theme.palette.primary.main};
    background-color: ${({ theme }) => theme.palette.dream.grayLighter};
  }

  &:focus-within {
    outline: none;
    border-color: ${({ theme }) => theme.palette.primary.main};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.palette.primary.main}33;
  }

  &.active {
    border-color: ${({ theme }) => theme.palette.primary.main};
    background-color: ${({ theme }) => theme.palette.dream.grayLighter};
  }

  ${({ $disabled }) =>
    $disabled &&
    `
    opacity: 0.5;
    pointer-events: none;
  `}
`;

const ChoiceInput = styled("input")`
  position: absolute;
  inset: 0;
  z-index: 1;
  width: 100%;
  height: 100%;
  margin: 0;
  opacity: 0;
  cursor: inherit;
`;

const IconWrapper = styled("div")`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 46px;
  width: 46px;
  height: 46px;
  border-radius: 50%;
  font-size: 1.2rem;
  color: ${({ theme }) => theme.palette.primary.main};
  background-color: ${({ theme }) => theme.palette.primary.main}12;
  line-height: 1;
`;

const IconPlaceholder = styled(Skeleton)`
  width: 1.25rem;
  height: 1.25rem;
  background-color: ${({ theme }) => theme.palette.primary.main}38;
`;

const LabelText = styled("span", {
  shouldForwardProp: (prop) => prop !== "$hasIcon",
})<{ $hasIcon: boolean }>`
  flex: 1;
  font-size: ${({ $hasIcon }) => ($hasIcon ? "1.05rem" : "1.02rem")};
  font-weight: 600;
  color: ${({ theme }) => theme.palette.text.primary};
  text-align: left;
  line-height: 1.4;
  transition: color 0.2s ease;
`;

const ChevronIcon = styled(FontAwesomeIcon)`
  flex: 0 0 auto;
  color: ${({ theme }) => theme.palette.dream.grayMedium};
  font-size: 0.875rem;
`;
